/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * People API endpoints.
 *
 * Mirrors the backend's /api/v1/people/* surface. Privacy gates are
 * enforced by the backend (visible_role_filter), so we don't need to
 * filter client-side. But we DO use the manifest to decide whether to
 * even surface certain UI (e.g., "create client" button hidden if user
 * isn't authorized).
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";
import type { MetricDrilldownData } from "@/shared/ui/MetricDrilldown";

import type {
  Interaction,
  InteractionSummary,
  RecentActivityRow,
  OrgRole,
  Organization,
  Person,
  PersonRole,
  Relationship,
} from "../model/types";

interface PersonListParams {
  page?: number;
  page_size?: number;
  search?: string;
  role_type?: string;
  /** `member`, `volunteer` or `staff` - the roster's three member types. */
  member_type?: string;
  role_status?: string;
  sub_org_slug?: string;
  ordering?: string;
  /** When "true", narrows the list to people favourited by the current user. */
  favorites?: string;
  /** `true` = archived only; `all` = active + archived; default active only. */
  terminated?: string;
}

/** A signed waiver or consent form. */
export interface ConsentRecord {
  id: number;
  person: number;
  form_type: string;
  form_label: string;
  signed_date: string;
  expires_date: string | null;
  signed_by: number | null;
  signed_by_name: string;
  notes: string;
  is_active: boolean;
}

/** Volunteer screening pipeline (v4.2.4). */
export interface VolunteerScreening {
  id: number;
  person: number;
  person_name: string;
  steps: ScreeningStep[];
  overall_status: "not_started" | "in_progress" | "cleared" | "denied";
  current_step: ScreeningStep | null;
  progress: number;
  started_at: string;
  completed_at: string | null;
  notes: string;
}

export interface ScreeningStep {
  key: string;
  label: string;
  order: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  completed_at: string | null;
  notes: string;
}

/** RTK Query endpoints for the people/CRM domain. */

/** Board report data (v4.3.0). */
export interface BoardReport {
  tenant_name: string;
  report_period: { start: string; end: string; days: number };
  metrics: {
    people_served: { current: number; prior: number; trend_pct: number | null };
    total_donations: { current: number; prior: number; trend_pct: number | null };
    interactions: { current: number; prior: number; trend_pct: number | null };
    volunteer_hours: { current: number };
    programs_active: number;
    grants_pipeline: number;
  };
  generated_at: string;
}

export interface EngagementScore {
  person_id: number;
  score: number;
  breakdown: {
    frequency: number;
    recency: number;
    events: number;
    volunteer: number;
    tenure: number;
  };
  inputs: {
    gifts_12m: number;
    days_since_last_gift: number | null;
    event_checkins: number;
    volunteer_hours: string;
    tenure_years: number;
  };
}

export interface DonorSummary {
  person_id?: number;
  organization_id?: number;
  lifetime_total: string;
  before_joining: string;
  since_joining: string;
  awaiting_review: string;
  awaiting_review_count: number;
  series_60d: { date: string; amount: string }[];
  gift_count: number;
  avg_gift: string;
  first_gift_date: string | null;
  last_gift_date: string | null;
  giving_frequency: string;
  trend: string;
  trend_pct: number;
  recent_year_total: string;
  prior_year_total: string;
  preferred_fund: string | null;
}

export const peopleApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listPersons: build.query<Paginated<Person>, PersonListParams>({
      query: (params) => ({
        url: "/people/persons/",
        params: params as Record<string, string | number>,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: "Person" as const, id })),
              { type: "Person", id: "LIST" },
            ]
          : [{ type: "Person", id: "LIST" }],
    }),

    listProgramCandidates: build.query<Person[], void>({
      query: () => "/people/persons/program-candidates/",
      transformResponse: (r: Person[] | { results?: Person[] } | null) => {
        if (Array.isArray(r)) return r;
        if (Array.isArray(r?.results)) return r.results;
        return [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Person" as const, id })),
              { type: "Person", id: "LIST" },
            ]
          : [{ type: "Person", id: "LIST" }],
    }),

    getPerson: build.query<Person, number>({
      query: (id) => `/people/persons/${id}/`,
      providesTags: (_, __, id) => [{ type: "Person", id }],
    }),

    getDonorSummary: build.query<DonorSummary, number>({
      query: (id) => `/people/persons/${id}/donor-summary/`,
      providesTags: (_, __, id) => [{ type: "Person", id: `DONOR-${id}` }],
    }),

    getOrgDonorSummary: build.query<DonorSummary, number>({
      query: (id) => `/people/organizations/${id}/donor-summary/`,
      providesTags: (_, __, id) => [{ type: "Organization", id: `DONOR-${id}` }],
    }),

    getEngagementScore: build.query<EngagementScore, number>({
      query: (id) => `/people/persons/${id}/engagement-score/`,
      providesTags: (_, __, id) => [{ type: "Person", id: `ENGAGE-${id}` }],
    }),

    /**
     * GET /api/v1/people/persons/:id/activity/
     *
     * Activity timeline for a single person: Interactions attached to any
     * of their roles that the viewer can see, newest-first, paginated.
     * The backend already filters by visible_role_filter so we don't need
     * to repeat the privacy logic client-side.
     */
    getPersonActivity: build.query<
      Paginated<Interaction>,
      { id: number; page?: number; page_size?: number }
    >({
      query: ({ id, page, page_size }) => ({
        url: `/people/persons/${id}/activity/`,
        params: {
          ...(page ? { page } : {}),
          ...(page_size ? { page_size } : {}),
        },
      }),
      providesTags: (_, __, { id }) => [
        { type: "Interaction", id: `PERSON-${id}` },
        { type: "Interaction", id: "LIST" },
      ],
    }),

    createPerson: build.mutation<Person, Partial<Person> & { role_type?: string }>({
      query: (body) => ({
        url: "/people/persons/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Person", id: "LIST" },
        { type: "Interaction", id: "SUMMARY" },
      ],
    }),

    updatePerson: build.mutation<Person, { id: number; data: Partial<Person> }>({
      query: ({ id, data }) => ({
        url: `/people/persons/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "Person", id },
        { type: "Person", id: "LIST" },
        { type: "Donor", id: "LIST" },
      ],
    }),

    /** End a membership without destroying the record. */
    terminatePerson: build.mutation<{ id: number; is_active: boolean }, number>({
      query: (id) => ({
        url: `/people/persons/${id}/terminate/`,
        method: "POST",
      }),
      invalidatesTags: (_, __, id) => [
        { type: "Person", id },
        { type: "Person", id: "LIST" },
      ],
    }),

    /** Put a terminated member back on the active roster. */
    restorePerson: build.mutation<{ id: number; is_active: boolean }, number>({
      query: (id) => ({
        url: `/people/persons/${id}/restore/`,
        method: "POST",
      }),
      invalidatesTags: (_, __, id) => [
        { type: "Person", id },
        { type: "Person", id: "LIST" },
      ],
    }),

    /** Toggle the signed-in user's favourite on a person. */
    toggleFavorite: build.mutation<{ id: number; is_favorite: boolean }, number>({
      query: (id) => ({
        url: `/people/persons/${id}/toggle-favorite/`,
        method: "POST",
      }),
      invalidatesTags: (_, __, id) => [
        { type: "Person", id },
        { type: "Person", id: "LIST" },
      ],
    }),

    deletePerson: build.mutation<void, number>({
      query: (id) => ({
        url: `/people/persons/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, id) => [
        { type: "Person", id },
        { type: "Person", id: "LIST" },
      ],
    }),

    listOrganizations: build.query<Paginated<Organization>, PersonListParams>({
      query: (params) => ({
        url: "/people/organizations/",
        params: params as Record<string, string | number>,
      }),
      providesTags: [{ type: "Organization", id: "LIST" }],
    }),

    getOrganization: build.query<Organization, number>({
      query: (id) => `/people/organizations/${id}/`,
      providesTags: (_, __, id) => [{ type: "Organization", id }],
    }),

    updateOrganization: build.mutation<Organization, { id: number; data: Partial<Organization> }>({
      query: ({ id, data }) => ({
        url: `/people/organizations/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Organization", id },
        { type: "Organization", id: "LIST" },
        { type: "Funder", id },
        { type: "Funder", id: "LIST" },
        { type: "Donor", id: "LIST" },
      ],
    }),

    listOrgRoles: build.query<
      Paginated<OrgRole>,
      { organization?: number; role_type?: string; status?: string; page_size?: number }
    >({
      query: (params) => ({
        url: "/people/org-roles/",
        params: params as Record<string, string | number>,
      }),
      providesTags: [{ type: "OrgRole", id: "LIST" }],
    }),

    deleteOrgRole: build.mutation<void, number>({
      query: (id) => ({ url: `/people/org-roles/${id}/`, method: "DELETE" }),
      invalidatesTags: [
        { type: "OrgRole", id: "LIST" },
        { type: "Organization", id: "LIST" },
        { type: "Funder", id: "LIST" },
      ],
    }),

    listPersonRoles: build.query<
      Paginated<PersonRole>,
      {
        person?: number;
        role_type?: string;
        status?: string;
        search?: string;
        page_size?: number;
      }
    >({
      query: (params) => ({
        url: "/people/person-roles/",
        params: params as Record<string, string | number>,
      }),
      providesTags: [{ type: "PersonRole", id: "LIST" }],
    }),

    listInteractions: build.query<
      Paginated<Interaction>,
      { subject_type?: string; subject_id?: number; page?: number; page_size?: number }
    >({
      query: (params) => ({
        url: "/people/interactions/",
        params: params as Record<string, string | number>,
      }),
      providesTags: [{ type: "Interaction", id: "LIST" }],
    }),

    createInteraction: build.mutation<Interaction, Partial<Interaction>>({
      query: (body) => ({
        url: "/people/interactions/",
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, body) => [
        { type: "Interaction", id: "LIST" },
        { type: "Interaction", id: "SUMMARY" },
        { type: "Funder", id: "LIST" },
        // getPersonActivity provides both `Interaction/PERSON-<id>` and
        // `Interaction/LIST`; the LIST tag above already refreshes any
        // open person timeline. When the subject is a PersonRole we
        // also refresh that role so a role-scoped view updates.
        ...(body.subject_type === "person_role" && body.subject_id
          ? [{ type: "PersonRole" as const, id: body.subject_id }]
          : []),
      ],
    }),

    /**
     * GET /api/v1/people/interactions/summary/
     *
     * Returns 30-day dashboard aggregates for the active tenant. Backed by
     * the InteractionViewSet.summary @action - one round-trip instead of
     * four list queries. Re-fetches whenever any Interaction changes
     * (which is when the numbers might shift).
     */
    getInteractionSummary: build.query<InteractionSummary, { window_days?: number } | void>({
      query: (params) => ({
        url: "/people/interactions/summary/",
        params: params?.window_days != null ? { window_days: params.window_days } : undefined,
      }),
      providesTags: [{ type: "Interaction", id: "SUMMARY" }],
    }),

    /**
     * GET /api/v1/people/interactions/recent-activity/
     *
     * Display-ready recent-activity feed for the dashboard's "What's
     * been happening" column - each row already carries the actor's
     * name, so no follow-up person lookups are needed.
     */
    getRecentActivity: build.query<{ results: RecentActivityRow[] }, { limit?: number } | void>({
      query: (params) => ({
        url: "/people/interactions/recent-activity/",
        params: (params || {}) as Record<string, string | number>,
      }),
      providesTags: [{ type: "Interaction", id: "LIST" }],
    }),

    /**
     * GET /api/v1/people/interactions/volunteer-hours-breakdown/
     *
     * Drilldown for the dashboard's "Volunteer hours this week" tile.
     * Returns the {summary, rows, notes, footer} popover contract.
     * Re-fetched whenever any volunteer shift changes.
     */
    getVolunteerHoursBreakdown: build.query<MetricDrilldownData, void>({
      query: () => "/people/interactions/volunteer-hours-breakdown/",
      providesTags: [{ type: "Interaction", id: "SUMMARY" }],
    }),

    /** Drilldown for "Donations this month" - gifts in last 30d
     *  bucketed by amount tier. */
    getDonationsBreakdown: build.query<MetricDrilldownData, void>({
      query: () => "/people/interactions/donations-breakdown/",
      providesTags: [{ type: "Interaction", id: "SUMMARY" }],
    }),

    /** Drilldown for "Programs running" - active programs with their
     *  recent-volunteer counts. */
    getProgramsBreakdown: build.query<MetricDrilldownData, void>({
      query: () => "/people/interactions/programs-breakdown/",
      providesTags: [{ type: "Interaction", id: "SUMMARY" }],
    }),

    /** Drilldown for "Active volunteers" - distinct active volunteers
     *  grouped by their most-recent program. */
    getActiveVolunteersBreakdown: build.query<MetricDrilldownData, void>({
      query: () => "/people/interactions/active-volunteers-breakdown/",
      providesTags: [{ type: "Interaction", id: "SUMMARY" }],
    }),

    /** Drilldown for "Upcoming events (7d)" - events in next 7d
     *  grouped by event_type. */
    getUpcomingEventsBreakdown: build.query<MetricDrilldownData, void>({
      query: () => "/people/interactions/upcoming-events-breakdown/",
      providesTags: [{ type: "Interaction", id: "SUMMARY" }],
    }),

    listRelationships: build.query<
      Paginated<Relationship>,
      { from_entity_type?: string; from_entity_id?: number }
    >({
      query: (params) => ({
        url: "/people/relationships/",
        params: params as Record<string, string | number>,
      }),
      providesTags: [{ type: "Relationship", id: "LIST" }],
    }),

    // v3.8 L2.C2 - Relationship + PersonRole CRUD.
    createRelationship: build.mutation<Relationship, Partial<Relationship>>({
      query: (body) => ({ url: "/people/relationships/", method: "POST", body }),
      invalidatesTags: [{ type: "Relationship", id: "LIST" }],
    }),
    updateRelationship: build.mutation<Relationship, { id: number; data: Partial<Relationship> }>({
      query: ({ id, data }) => ({
        url: `/people/relationships/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "Relationship", id: "LIST" }],
    }),
    deleteRelationship: build.mutation<void, number>({
      query: (id) => ({ url: `/people/relationships/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "Relationship", id: "LIST" }],
    }),

    createPersonRole: build.mutation<PersonRole, Partial<PersonRole> & { person?: number }>({
      query: (body) => ({ url: "/people/person-roles/", method: "POST", body }),
      invalidatesTags: (_r, _e, body) => [
        { type: "PersonRole", id: "LIST" },
        ...(body.person ? [{ type: "Person" as const, id: body.person }] : []),
      ],
    }),
    updatePersonRole: build.mutation<PersonRole, { id: number; data: Partial<PersonRole> }>({
      query: ({ id, data }) => ({
        url: `/people/person-roles/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "PersonRole", id: "LIST" }],
    }),
    deletePersonRole: build.mutation<void, number>({
      query: (id) => ({ url: `/people/person-roles/${id}/`, method: "DELETE" }),
      invalidatesTags: [{ type: "PersonRole", id: "LIST" }],
    }),
    bulkAssignPersonRoles: build.mutation<
      { created: number; skipped: number; total: number },
      { person_ids: number[]; role_type: string }
    >({
      query: (body) => ({
        url: "/people/person-roles/bulk-assign/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "PersonRole", id: "LIST" },
        { type: "Person", id: "LIST" },
      ],
    }),

    listConsentRecords: build.query<ConsentRecord[], number>({
      query: (personId) => `/people/consent-records/?person=${personId}`,
      transformResponse: (r: ConsentRecord[] | { results?: ConsentRecord[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r: ConsentRecord[] | undefined, _e: unknown, personId: number) => [
        { type: "Person" as const, id: `consent-${personId}` },
      ],
    }),

    createConsentRecord: build.mutation<ConsentRecord, Partial<ConsentRecord>>({
      query: (data) => ({
        url: "/people/consent-records/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (
        _r: ConsentRecord | undefined,
        _e: unknown,
        data: Partial<ConsentRecord>,
      ) => [{ type: "Person" as const, id: `consent-${data.person}` }],
    }),

    listScreenings: build.query<VolunteerScreening[], void>({
      query: () => "/people/screenings/",
      transformResponse: (r: { results?: VolunteerScreening[] } | VolunteerScreening[]) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Person" as const, id: "SCREENINGS" }],
    }),

    getPersonScreening: build.query<VolunteerScreening | undefined, number>({
      query: (personId) => `/people/screenings/?person=${personId}`,
      transformResponse: (r: { results?: VolunteerScreening[] } | VolunteerScreening[]) => {
        const list = Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [];
        return list[0];
      },
      providesTags: (_r, _e, personId) => [
        { type: "Person" as const, id: `screen-${personId}` },
        { type: "Person" as const, id: "SCREENINGS" },
      ],
    }),

    createScreening: build.mutation<VolunteerScreening, { person: number }>({
      query: (data) => ({
        url: "/people/screenings/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_r, _e, data) => [
        { type: "Person" as const, id: "SCREENINGS" },
        { type: "Person" as const, id: `screen-${data.person}` },
      ],
    }),

    advanceScreeningStep: build.mutation<
      VolunteerScreening,
      { screeningId: number; step_key: string; status: string; notes?: string }
    >({
      query: ({ screeningId, ...body }) => ({
        url: `/people/screenings/${screeningId}/advance/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Person" as const, id: "SCREENINGS" }],
    }),

    getBoardReport: build.query<BoardReport, void>({
      query: () => "/people/board-report/",
    }),
  }),
  overrideExisting: false,
});

export const {
  useListPersonsQuery,
  useLazyListPersonsQuery,
  useListProgramCandidatesQuery,
  useGetPersonQuery,
  useGetDonorSummaryQuery,
  useGetOrgDonorSummaryQuery,
  useGetEngagementScoreQuery,
  useGetPersonActivityQuery,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useToggleFavoriteMutation,
  useTerminatePersonMutation,
  useRestorePersonMutation,
  useDeletePersonMutation,
  useListOrganizationsQuery,
  useLazyListOrganizationsQuery,
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
  useListOrgRolesQuery,
  useDeleteOrgRoleMutation,
  useListPersonRolesQuery,
  useListInteractionsQuery,
  useCreateInteractionMutation,
  useGetInteractionSummaryQuery,
  useGetRecentActivityQuery,
  useGetVolunteerHoursBreakdownQuery,
  useGetDonationsBreakdownQuery,
  useGetProgramsBreakdownQuery,
  useGetActiveVolunteersBreakdownQuery,
  useGetUpcomingEventsBreakdownQuery,
  useListRelationshipsQuery,
  useCreateRelationshipMutation,
  useUpdateRelationshipMutation,
  useDeleteRelationshipMutation,
  useCreatePersonRoleMutation,
  useUpdatePersonRoleMutation,
  useDeletePersonRoleMutation,
  useBulkAssignPersonRolesMutation,
  useListConsentRecordsQuery,
  useCreateConsentRecordMutation,
  useListScreeningsQuery,
  useGetPersonScreeningQuery,
  useCreateScreeningMutation,
  useAdvanceScreeningStepMutation,
  useGetBoardReportQuery,
} = peopleApi;

// (unused export to keep the type imported - aids tree-shaking visibility)
export type { OrgRole };
